/**
 * `<NoteMetadataGrid>` — 4-column read-only grid that sits between the
 * Progress Note title row and the Stats Strip. Mirrors the mockup at
 * `ui-designs/.../ChartPage.tsx:3990-3997`.
 *
 * Column pairs (label / value):
 *   • Source         · Added on
 *   • Type           · Modified on
 *   • Document Name  · (empty)
 *   • Provider       · Encounter Date
 *
 * Data is sourced from the DocumentReference + optional Provenance/Encounter.
 * Source / Provider come from `Provenance.agent` (canonical), matching the
 * helpers in `Documents.tsx:464-486`.
 *
 * Practitioner fallback: older notes were saved with `who.display = 'N/A'`
 * because the BE didn't backfill the display from the Practitioner resource
 * (fixed in `visit.service.ts:evaluateSoapNote` for future writes). When the
 * cached display is empty / "N/A", we hydrate from `who.reference` so the
 * provider row still renders the real name.
 */
import { Group, SimpleGrid, Text } from '@mantine/core'
import { useMedplum } from '@medplum/react'
import { useQuery } from '@tanstack/react-query'
import { formatHumanName } from '@medplum/core'
import type { DocumentReference, Encounter, Provenance } from '@medplum/fhirtypes'
import { formatDateTime } from '@/utils'

export type NoteMetadataGridProps = {
  documentReference: DocumentReference
  encounter?: Encounter
  provenance?: Provenance
}

function findAgent(provenance: Provenance | undefined, agentCode: 'source' | 'performer') {
  return provenance?.agent?.find((a) => a.type?.coding?.some((c) => c.code === agentCode))
}

function isMissingDisplay(display: string | undefined): boolean {
  if (!display) return true
  const trimmed = display.trim().toLowerCase()
  return trimmed === '' || trimmed === 'n/a' || trimmed === '-' || trimmed === '—'
}

function resolveSource(provenance?: Provenance): string {
  const display = findAgent(provenance, 'source')?.who?.display
  return isMissingDisplay(display) ? '—' : display!
}

function resolveType(documentReference: DocumentReference): string {
  if (documentReference.type?.text) return documentReference.type.text
  const coding = documentReference.type?.coding?.[0]
  return coding?.display || coding?.code || '—'
}

function resolveDocumentName(documentReference: DocumentReference): string {
  return documentReference.description || resolveType(documentReference)
}

// "Encounter Date" must never borrow the note's created date — encounter date or nothing.
function resolveEncounterDate(encounter?: Encounter): string {
  const start = encounter?.period?.start
  return start ? formatDateTime(start, '-', true) : '—'
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <Group gap="xs" align="flex-start" wrap="nowrap">
      <Text
        style={{
          width: 120,
          flexShrink: 0,
          fontSize: 12,
          fontWeight: 600,
          color: '#6b7280',
        }}
      >
        {label}
      </Text>
      <Text style={{ fontSize: 13, color: '#111827' }}>{value}</Text>
    </Group>
  )
}

/**
 * Resolve the Provider display name. Prefers the cached `performer.who.display`
 * on the Provenance; falls back to a Practitioner read against
 * `performer.who.reference` for legacy notes that were saved with
 * `display: 'N/A'`.
 */
function useProviderDisplay(provenance: Provenance | undefined): string {
  const medplum = useMedplum()
  const performer = findAgent(provenance, 'performer')
  const cachedDisplay = performer?.who?.display
  const reference = performer?.who?.reference

  const needsFetch =
    isMissingDisplay(cachedDisplay) && !!reference && reference.startsWith('Practitioner/')

  const { data } = useQuery({
    enabled: needsFetch,
    queryKey: ['note-metadata-provider', reference],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const id = reference!.split('/')[1]
      const practitioner = await medplum.readResource('Practitioner', id)
      return practitioner.name?.[0] ? formatHumanName(practitioner.name[0]) : null
    },
  })

  if (!isMissingDisplay(cachedDisplay)) return cachedDisplay!
  return data || '—'
}

export function NoteMetadataGrid({
  documentReference,
  encounter,
  provenance,
}: NoteMetadataGridProps) {
  const addedOn = documentReference.date ? formatDateTime(documentReference.date, '-', true) : '—'
  const modifiedOn = documentReference.meta?.lastUpdated
    ? formatDateTime(documentReference.meta.lastUpdated, '-', true)
    : '—'
  const providerDisplay = useProviderDisplay(provenance)

  return (
    <SimpleGrid
      cols={{ base: 1, sm: 2 }}
      spacing="sm"
      verticalSpacing={6}
      mb="md"
      data-testid="note-metadata-grid"
    >
      <Row label="Source" value={resolveSource(provenance)} />
      <Row label="Added on" value={addedOn} />
      <Row label="Type" value={resolveType(documentReference)} />
      <Row label="Modified on" value={modifiedOn} />
      <Row label="Document Name" value={resolveDocumentName(documentReference)} />
      <span />
      <Row label="Provider" value={providerDisplay} />
      <Row label="Encounter Date" value={resolveEncounterDate(encounter)} />
    </SimpleGrid>
  )
}
